/* eslint-disable react-refresh/only-export-components */
import { useEffect } from 'react';

import { User } from '../pages/user/interface';
import { useAPI } from '../state';

// API endpoint constants
const USER_SESSION_API = '/api/v1/users/session';
const LOGOUT_API = '/api/v1/logout';

/**
 * Hook for fetching user session data
 * @returns {Array} [userData, fetchUserSessionData, clearUserSessionData]
 */
export const useUserSessionAPI = () => {
  return useAPI<User>(USER_SESSION_API);
};

/**
 * Hook to access current user session data
 * @returns {User|undefined} Current user data if authenticated
 */
export const useUserSessionData = () => {
  const [userData] = useUserSessionAPI();
  return userData.data;
};

/**
 * Component that automatically loads user session data on mount
 * Should be placed high in the component tree (e.g., App component)
 */
export const UserSessionLoader = () => {
  const [, fetchUserSessionData] = useUserSessionAPI();

  useEffect(() => {
    fetchUserSessionData();
  }, [fetchUserSessionData]);

  return null;
};

/**
 * Hook to perform logout action
 * @returns {Array} [response, logout, clearResponse]
 */
export const useLogout = () => {
  return useAPI(LOGOUT_API, {}, 'POST');
};

/**
 * Hook to check if user has a specific permission
 * @param {string} permission - Permission to check
 * @returns {boolean} True if user has the permission
 */
export const useCheckSinglePermission = (permission: string) => {
  const userSessionData = useUserSessionData();
  return userSessionData?.permissions.includes(permission);
};

/**
 * Hook to check if user has all specified permissions
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} True if user has all permissions
 */
export const useCheckMultiplePermissions = (permissions: string[]) => {
  const userSessionData = useUserSessionData();
  return permissions.every((permission) =>
    userSessionData?.permissions.includes(permission)
  );
};
